import { useState, useEffect } from "react";
import axios from "axios";
import { Building2, Scale, ShieldCheck, ChevronDown, ChevronUp } from "lucide-react";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend, Tooltip } from "recharts";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const DIM_LABELS = {
  care_provisions: "Care", diversity_provisions: "Diversity", governance_provisions: "Governance",
  geographic_equity: "Geo Equity", extraction_restrictions: "Extraction Limits",
  inclusion_mandates: "Inclusion", agency_protections: "Agency",
};

const LabCard = ({ lab, nationalAvg }) => {
  const [open, setOpen] = useState(false);
  const delta = (lab.avg_cii - nationalAvg).toFixed(1);
  return (
    <div data-testid="lab-card" className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
      <button data-testid={`lab-toggle-${lab.lab.toLowerCase().replace(/\s/g, "-")}`} onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-3 text-left">
        <div className="flex items-center gap-2 min-w-0">
          <Building2 className="w-4 h-4 text-amber-400 shrink-0" />
          <p className="text-sm font-bold text-white truncate">{lab.lab}</p>
          <span className="text-[9px] text-slate-500">{lab.framework_count} framework{lab.framework_count > 1 ? "s" : ""}</span>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="text-right">
            <p className="text-base font-black text-amber-400">{lab.avg_cii}</p>
            <p className={`text-[8px] font-semibold ${delta >= 0 ? "text-emerald-400" : "text-red-400"}`}>{delta >= 0 ? "+" : ""}{delta} vs national avg</p>
          </div>
          {open ? <ChevronUp className="w-3.5 h-3.5 text-slate-500" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-500" />}
        </div>
      </button>
      {open && (
        <div className="mt-3 space-y-1.5">
          {lab.frameworks.map(f => (
            <div key={f.name} className="flex items-center justify-between gap-3 bg-white/[0.02] border border-white/[0.04] rounded-lg px-3 py-2">
              <p className="text-[11px] text-slate-300 leading-snug">{f.name} <span className="text-slate-600">· {f.year}</span></p>
              <span className="text-[10px] font-bold text-amber-400/90 shrink-0">CII {f.cii}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const CorporatePolicyLens = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/policy/corporate-lens`, { timeout: 30000 })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-center text-slate-500 text-sm">Loading corporate lens…</div>;
  if (!data || !data.labs?.length) return <div className="p-8 text-center text-slate-500 text-sm">No corporate frameworks tracked yet.</div>;

  const radarData = Object.keys(DIM_LABELS).map(d => ({
    dimension: DIM_LABELS[d],
    Corporate: data.corporate.dimension_scores[d] ?? 0,
    National: data.national.dimension_scores[d] ?? 0,
  }));

  return (
    <div data-testid="corporate-policy-lens" className="space-y-6 max-w-6xl mx-auto">
      <div>
        <h2 className="text-xl font-black text-white flex items-center gap-2"><Scale className="w-5 h-5 text-amber-400" />Corporate Policy Lens</h2>
        <p className="text-xs text-slate-400 mt-1">How AI lab safety frameworks stack up against national regulation — same 7 CII dimensions, side by side.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-amber-500/[0.05] border border-amber-500/15 rounded-xl p-4">
          <p className="text-2xl font-black text-amber-400">{data.corporate.policy_count}</p>
          <p className="text-[9px] text-slate-500 uppercase tracking-wider">Lab frameworks ({data.corporate.labs} labs)</p>
        </div>
        <div className="bg-amber-500/[0.05] border border-amber-500/15 rounded-xl p-4">
          <p className="text-2xl font-black text-white">{data.corporate.avg_cii}</p>
          <p className="text-[9px] text-slate-500 uppercase tracking-wider">Corporate avg CII</p>
        </div>
        <div className="bg-cyan-500/[0.05] border border-cyan-500/15 rounded-xl p-4">
          <p className="text-2xl font-black text-cyan-400">{data.national.avg_cii}</p>
          <p className="text-[9px] text-slate-500 uppercase tracking-wider">National avg CII ({data.national.jurisdictions} jurisdictions)</p>
        </div>
        <div className="bg-cyan-500/[0.05] border border-cyan-500/15 rounded-xl p-4">
          <p className="text-2xl font-black text-white">{data.national.mandatory_pct}%<span className="text-xs text-slate-500 font-semibold"> vs {data.corporate.mandatory_pct}%</span></p>
          <p className="text-[9px] text-slate-500 uppercase tracking-wider">Mandatory: national vs corporate</p>
        </div>
      </div>

      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck className="w-4 h-4 text-amber-400" />
          <p className="text-[10px] font-bold text-amber-400/80 uppercase tracking-[0.2em]">Dimension Coverage — Corporate vs National</p>
        </div>
        <div className="h-[340px] sm:h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={radarData} outerRadius="72%">
              <PolarGrid stroke="rgba(255,255,255,0.08)" />
              <PolarAngleAxis dataKey="dimension" tick={{ fill: "#94a3b8", fontSize: 10, fontWeight: 600 }} />
              <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
              <Radar name="Corporate labs" dataKey="Corporate" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.25} strokeWidth={2} />
              <Radar name="National regulation" dataKey="National" stroke="#06b6d4" fill="#06b6d4" fillOpacity={0.2} strokeWidth={2} />
              <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
        <p className="text-[10px] text-slate-500">% of frameworks in each group containing provisions for that dimension. Corporate frameworks are voluntary self-governance; national figures cover {data.national.policy_count} national and regional instruments.</p>
      </div>

      <div className="space-y-2">
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">Lab-by-lab breakdown (ranked by avg CII)</p>
        {data.labs.map(lab => <LabCard key={lab.lab} lab={lab} nationalAvg={data.national.avg_cii} />)}
      </div>
    </div>
  );
};
